import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useConfirm } from "@/context/ConfirmContext";
import { toast } from "sonner";
import { UserPlus, Check, X, Clock, Mail } from "lucide-react";
import { format, parseISO } from "date-fns";

const SERVICE_OPTIONS = ["makeup", "photography", "mehendi", "videography", "hair", "assistant"];

function timeAgo(iso) {
  try { return format(parseISO(iso), "dd MMM yyyy, HH:mm"); } catch { return iso; }
}

function ApproveModal({ open, req, onClose, onDone }) {
  const [role, setRole] = useState("team_member");
  const [services, setServices] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open) { setRole("team_member"); setServices([]); } }, [open]);
  if (!open || !req) return null;

  const toggle = (s) => setServices(services.includes(s) ? services.filter((x) => x !== s) : [...services, s]);

  const approve = async () => {
    setSaving(true);
    try {
      await api.post(`/access-requests/${req.id}/approve`, { role, service_types: role === "team_member" ? services : [] });
      toast.success(`${req.name || req.email} approved`);
      window.dispatchEvent(new Event("admin-data-updated"));
      onDone(); onClose();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start md:items-center justify-center p-4 overflow-y-auto" data-testid="approve-access-modal">
      <div className="bg-white border border-[#EAE7E0] max-w-md w-full my-8">
        <div className="flex items-center justify-between p-6 border-b border-[#EAE7E0]">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#5C5652]">Approve access</div>
            <h2 className="font-serif text-2xl tracking-tight">{req.name || req.email}</h2>
            <div className="text-xs text-[#5C5652] mt-1">{req.email}</div>
          </div>
          <button onClick={onClose} className="text-[#5C5652] hover:text-[#2A2421]" data-testid="close-approve-modal"><X size={20} /></button>
        </div>
        <div className="p-6 space-y-5">
          <div>
            <label className="block text-[10px] uppercase tracking-[0.25em] text-[#5C5652] mb-2">Role</label>
            <select value={role} onChange={(e) => setRole(e.target.value)} data-testid="approve-role-select"
              className="w-full border-b border-[#EAE7E0] bg-transparent py-2 focus:border-[#C88270] focus:outline-none">
              <option value="team_member">Team Member</option>
              <option value="admin">Admin</option>
            </select>
          </div>
          {role === "team_member" && (
            <div>
              <label className="block text-[10px] uppercase tracking-[0.25em] text-[#5C5652] mb-3">Services</label>
              <div className="flex flex-wrap gap-2">
                {SERVICE_OPTIONS.map((s) => (
                  <button type="button" key={s} onClick={() => toggle(s)} data-testid={`approve-service-${s}`}
                    className={`px-4 py-2 text-xs uppercase tracking-[0.2em] border transition-colors ${services.includes(s) ? "bg-[#C88270] text-white border-[#C88270]" : "border-[#EAE7E0] text-[#5C5652] hover:border-[#2A2421] hover:text-[#2A2421]"}`}>{s}</button>
                ))}
              </div>
            </div>
          )}
          <div className="flex justify-end gap-3 pt-4 border-t border-[#EAE7E0]">
            <button type="button" onClick={onClose} className="px-6 py-2 text-xs uppercase tracking-[0.2em] text-[#5C5652]">Cancel</button>
            <button onClick={approve} disabled={saving} data-testid="confirm-approve-button"
              className="px-6 py-2 bg-[#829384] text-white text-xs uppercase tracking-[0.25em] hover:bg-[#6d7c6f] transition-colors disabled:opacity-50">
              {saving ? "Approving…" : "Approve & add"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function AccessRequestsPage() {
  const [reqs, setReqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [approveReq, setApproveReq] = useState(null);
  const confirm = useConfirm();

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/access-requests"); setReqs(data); }
    catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const deny = async (req) => {
    if (!(await confirm({ title: "Deny access?", description: `${req.name || req.email} will not be able to log in. They can request again later.`, confirmText: "Deny" }))) return;
    try {
      await api.delete(`/access-requests/${req.id}`);
      toast.success("Request denied");
      window.dispatchEvent(new Event("admin-data-updated"));
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="max-w-4xl mx-auto px-6 md:px-12 py-10 md:py-16" data-testid="access-requests-page">
      <div className="mb-10">
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#5C5652] mb-3">Studio</div>
        <h1 className="font-serif text-4xl md:text-5xl tracking-tight text-[#2A2421] flex items-center gap-4">
          <UserPlus size={34} strokeWidth={1.5} className="text-[#C88270]" /> Access Requests
        </h1>
        <p className="text-[#5C5652] mt-3">People who tried to sign in with Google but aren't on your team yet. Approve to grant access.</p>
      </div>

      {loading ? (
        <div className="text-xs uppercase tracking-[0.3em] text-[#5C5652]">Loading…</div>
      ) : reqs.length === 0 ? (
        <div className="p-12 bg-white border border-[#EAE7E0] text-center text-[#5C5652]" data-testid="no-access-requests">
          No pending access requests.
        </div>
      ) : (
        <div className="space-y-4">
          {reqs.map((r) => (
            <div key={r.id} className="p-5 md:p-6 bg-white border border-[#EAE7E0] flex flex-col md:flex-row md:items-center gap-4" data-testid={`access-request-${r.id}`}>
              <div className="flex items-center gap-4 flex-1 min-w-0">
                {r.picture ? (
                  <img src={r.picture} alt={r.name || r.email} className="w-12 h-12 rounded-full object-cover shrink-0" />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-[#FAF9F6] border border-[#EAE7E0] flex items-center justify-center text-[#C88270] shrink-0">
                    <Mail size={18} />
                  </div>
                )}
                <div className="min-w-0">
                  <div className="font-serif text-xl tracking-tight text-[#2A2421] truncate">{r.name || r.email.split("@")[0]}</div>
                  <div className="text-sm text-[#5C5652] truncate">{r.email}</div>
                  <div className="text-[11px] text-[#B8B2AA] mt-1 flex items-center gap-1">
                    <Clock size={12} /> Last tried {timeAgo(r.last_attempt_at || r.requested_at)}
                    {r.attempts > 1 && <span className="ml-2">· {r.attempts} attempts</span>}
                  </div>
                </div>
              </div>
              <div className="flex gap-3 shrink-0">
                <button onClick={() => setApproveReq(r)} data-testid={`approve-access-${r.id}`}
                  className="inline-flex items-center gap-2 px-5 py-2 bg-[#829384] text-white text-xs uppercase tracking-[0.2em] hover:bg-[#6d7c6f] transition-colors">
                  <Check size={14} /> Approve
                </button>
                <button onClick={() => deny(r)} data-testid={`deny-access-${r.id}`}
                  className="inline-flex items-center gap-2 px-5 py-2 border border-red-700 text-red-700 text-xs uppercase tracking-[0.2em] hover:bg-red-700 hover:text-white transition-colors">
                  <X size={14} /> Deny
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <ApproveModal open={!!approveReq} req={approveReq} onClose={() => setApproveReq(null)} onDone={load} />
    </div>
  );
}
